
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from "react";
import { useProducts } from "../../hooks/useProducts";
import { Product } from "../../types/products";
import { Search, Plus, Minus, CreditCard, QrCode, ShoppingBag, CheckCircle, Smartphone, Zap, Ticket, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../ui/Card";
import { useWallet } from "../../hooks/useWallet";
import { Button } from "../ui/Button";
import { CheckoutModal } from "./pos/CheckoutModal";
import { MintTicketModal } from "./pos/MintTicketModal";
import { verifyNFTOwnership } from "../../services/nft";

interface POSTransaction {
  id: string;
  label: string;
  amount: number;
  time: string;
  method: string;
}

export const POSView: React.FC = () => {
  const { products } = useProducts();
  const [cart, setCart] = useState<{product: Product, quantity: number}[]>([]);
  const [search, setSearch] = useState("");
  const { walletAddress, balances, refreshBalances } = useWallet();
  const [paymentMethod, setPaymentMethod] = useState<'SUI' | 'SOL' | 'USDC' | 'CARD'>('SUI');
  const [transactions, setTransactions] = useState<POSTransaction[]>([
      { id: '1', label: 'Order #1045', amount: 45.00, time: '10:00', method: 'SUI' },
      { id: '2', label: 'Order #1046', amount: 120.00, time: '10:30', method: 'SOL' },
  ]);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showMintTicketModal, setShowMintTicketModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [gatingError, setGatingError] = useState<string | null>(null);
  
  const tokenMap = useMemo(() => new Map(balances.map(token => [token.type, token])), [balances]);

  const categories = useMemo(() => {
    const allCategories = products.map(p => p.category);
    return ['All', ...Array.from(new Set(allCategories))];
  }, [products]);

  const addToCart = async (product: Product) => {
    if (product.isNftGated && product.nftCollection) {
        if (!walletAddress) {
            setGatingError("Wallet required for NFT verification.");
            setTimeout(() => setGatingError(null), 3000);
            return;
        }
        const hasNft = await verifyNFTOwnership(walletAddress, product.nftCollection);
        if (!hasNft) {
            setGatingError("Access Denied: Missing Required NFT.");
            setTimeout(() => setGatingError(null), 3000);
            return;
        }
    }

    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => item.product.id === product.id ? {...item, quantity: item.quantity + 1} : item);
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === productId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => item.product.id === productId ? {...item, quantity: item.quantity - 1} : item);
      }
      return prev.filter(item => item.product.id !== productId);
    });
  };

  const total = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  
  const networkFee = useMemo(() => {
    if (total === 0) return 0;
    switch (paymentMethod) {
        case 'SOL': return 0.05;
        case 'SUI': return 0.02;
        case 'USDC': return 0.08;
        case 'CARD': return total * 0.02;
        default: return 0;
    }
  }, [total, paymentMethod]);

  const finalTotal = total + networkFee;

  const handleCheckout = () => {
      if (finalTotal > 0) {
          setShowCheckoutModal(true);
      }
  };

  const handlePaymentSuccess = async () => {
      const newTx: POSTransaction = {
          id: Date.now().toString(),
          label: `Order #${transactions.length + 1047}`,
          amount: finalTotal,
          time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
          method: paymentMethod
      };
      
      setTransactions(prev => [...prev, newTx]);
      setCart([]);
      setShowSuccessToast(true);
      
      // Update global wallet state to reflect new on-chain balances
      await refreshBalances();
      
      setTimeout(() => setShowSuccessToast(false), 4000);
  };

  const filteredProducts = products.filter(p => 
      p.name.toLowerCase().includes(search.toLowerCase()) &&
      (activeCategory === 'All' || p.category === activeCategory)
  );


  return (
    <div className="flex flex-col lg:flex-row h-full gap-6">
       <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left">
             <div className="space-y-1">
                <h2 className="font-serif text-4xl text-[#0A0B0F] dark:text-white font-bold tracking-tight">GhostPOS</h2>
                <p className="text-gray-500 text-sm">Point of sale for modern businesses.</p>
             </div>
             <div className="flex gap-3">
                 <Button onClick={() => setShowMintTicketModal(true)} variant="outline" size="sm" icon={<Ticket size={16}/>}>
                     Create Tickets
                 </Button>
                 <div className="px-4 py-2 bg-white dark:bg-white/10 border border-gray-200 dark:border-white/5 rounded-lg flex items-center gap-2 text-xs font-bold text-[#0A0B0F] dark:text-white uppercase tracking-wider">
                    <div className={`w-2 h-2 rounded-full ${walletAddress ? 'bg-green-500 shadow-[0_0_5px_#22c55e]' : 'bg-red-500'}`}></div>
                    {walletAddress ? 'Terminal Online' : 'Terminal Offline'}
                 </div>
             </div>
          </div>

          <Card className="flex-1 flex flex-col overflow-hidden">
             <div className="p-4 border-b border-gray-100 dark:border-white/5">
                <div className="relative">
                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                   <input 
                      type="text" 
                      placeholder="Search inventory..." 
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/5 rounded-lg focus:border-[#0A0B0F] dark:focus:border-white outline-none text-[#0A0B0F] dark:text-white transition-colors"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                   />
                </div>
             </div>
             
            <div className="px-4 pt-4 border-b border-gray-100 dark:border-white/5">
                <div className="flex gap-2 overflow-x-auto no-scrollbar -mb-px">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`px-4 py-3 rounded-t-lg text-[10px] uppercase tracking-widest font-bold whitespace-nowrap transition-all duration-200 border-b-2 ${
                        activeCategory === category
                          ? 'border-nobel-gold text-[#0A0B0F] dark:text-white'
                          : 'border-transparent text-gray-500 hover:text-[#0A0B0F] dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-700'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
            </div>

             <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-gray-50/50 dark:bg-[#0A0B0F]/50">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                   {filteredProducts.map(product => (
                      <button 
                         key={product.id}
                         onClick={() => addToCart(product)}
                         className="bg-white dark:bg-[#15171C] border border-gray-200 dark:border-white/10 p-3 rounded-xl hover:border-nobel-gold dark:hover:border-nobel-gold hover:shadow-lg transition-all text-left group flex flex-col relative"
                      >
                         {product.isNftGated && (
                             <div className="absolute top-2 right-2 z-10 bg-black/80 text-white p-1 rounded-full border border-white/20">
                                 <Lock size={12} />
                             </div>
                         )}
                         <div className="w-full aspect-square bg-gray-50 dark:bg-white/5 rounded-lg mb-3 group-hover:scale-105 transition-transform duration-300 flex items-center justify-center overflow-hidden">
                            <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain p-2" />
                         </div>
                         <div className="flex-1 flex flex-col">
                            <div className="font-bold text-[#0A0B0F] dark:text-white text-sm leading-tight line-clamp-2 flex-grow">{product.name}</div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs font-mono mt-1 flex justify-between items-center">
                                <span>${product.price.toFixed(2)}</span>
                                {product.isTicket && <Ticket size={12} className="text-nobel-gold" />}
                            </div>
                         </div>
                      </button>
                   ))}
                </div>
             </div>
          </Card>
       </div>

       <div className="w-full lg:w-[420px] bg-white dark:bg-[#15171C] rounded-card border border-gray-200 dark:border-[#2D3142] shadow-smooth dark:shadow-smooth-dark flex flex-col h-full overflow-hidden">
          <div className="p-6 border-b border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-[#0A0B0F]">
             <h3 className="font-serif text-xl text-[#0A0B0F] dark:text-white font-bold">New Sale</h3>
             <div className="text-[10px] uppercase font-bold text-gray-400 flex justify-between tracking-widest mt-1">
                 <span>Ticket #1045</span>
                 <span>{new Date().toLocaleDateString()}</span>
             </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
             {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-50">
                   <ShoppingBag size={48} strokeWidth={1} />
                   <p className="mt-4 text-sm font-medium">Cart is empty</p>
                </div>
             ) : (
                cart.map(item => (
                   <div key={item.product.id} className="flex items-center gap-3">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-lg flex items-center justify-center p-1 flex-shrink-0">
                         <img src={item.product.image} alt={item.product.name} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                         <div className="text-sm font-bold text-[#0A0B0F] dark:text-white truncate">{item.product.name}</div>
                         <div className="text-xs text-gray-500">${item.product.price.toFixed(2)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                         <button onClick={() => removeFromCart(item.product.id)} className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 flex items-center justify-center text-gray-600 dark:text-white transition-colors">
                            <Minus size={12} />
                         </button>
                         <span className="text-sm font-mono font-bold w-5 text-center text-[#0A0B0F] dark:text-white">{item.quantity}</span>
                         <button onClick={() => addToCart(item.product)} className="w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 flex items-center justify-center text-gray-600 dark:text-white transition-colors">
                            <Plus size={12} />
                         </button>
                      </div>
                      <div className="w-20 text-right font-mono font-bold text-sm text-[#0A0B0F] dark:text-white">
                        ${(item.product.price * item.quantity).toFixed(2)}
                      </div>
                   </div>
                ))
             )}
          </div>

          <div className="p-6 border-t border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-[#0A0B0F]">
             <div className="space-y-2 mb-6">
                <div className="flex justify-between text-xs text-gray-500 uppercase tracking-widest font-bold">
                   <span>Subtotal</span>
                   <span className="font-mono">${total.toFixed(2)}</span>
                </div>
                 <div className="flex justify-between text-xs text-gray-500 uppercase tracking-widest font-bold">
                   <span>Fees & Network</span>
                   <span className="font-mono">${networkFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-[#0A0B0F] dark:text-white pt-3 border-t border-gray-200 dark:border-white/10 mt-2 !mb-6">
                   <span>Total</span>
                   <span className="font-mono">${finalTotal.toFixed(2)}</span>
                </div>
             </div>
             
             <div className="space-y-3 mb-6">
                <div className="text-[10px] uppercase font-bold tracking-widest text-stone-400">Select Payment Method</div>
                <div className="grid grid-cols-3 gap-2">
                    {(['SUI', 'SOL', 'USDC'] as const).map(method => {
                        const token = tokenMap.get(method);
                        return (
                            <button 
                                key={method}
                                onClick={() => setPaymentMethod(method)} 
                                className={`flex flex-col items-center justify-center gap-1 p-2 border rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${paymentMethod === method ? 'bg-nobel-gold border-nobel-gold text-black shadow-lg scale-105' : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-stone-600 dark:text-gray-300 hover:border-gray-300'}`}
                            >
                                {token ? <img src={token.icon} alt={token.symbol} className="w-5 h-5 rounded-full" /> : <Zap size={14}/>}
                                <span>{method}</span>
                            </button>
                        )
                    })}
                </div>
                
                <button 
                    onClick={() => setPaymentMethod('CARD')}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${paymentMethod === 'CARD' ? 'bg-nobel-gold border-nobel-gold text-black shadow-lg' : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-stone-600 dark:text-gray-300 hover:border-gray-300'}`}
                >
                    <div className="flex items-center gap-3 text-left">
                        <div className={`p-2 rounded-lg ${paymentMethod === 'CARD' ? 'bg-black/10' : 'bg-stone-100 dark:bg-white/5'}`}>
                            <CreditCard size={20} />
                        </div>
                        <div>
                            <div className="font-bold text-sm">Card Reader</div>
                            <div className="text-[10px] opacity-70 uppercase font-bold tracking-widest">Connect Terminal</div>
                        </div>
                    </div>
                    {paymentMethod === 'CARD' && <CheckCircle size={18} />}
                </button>
             </div>

             <Button 
                variant="primary"
                onClick={handleCheckout} 
                disabled={cart.length === 0}
                className="w-full py-4 text-base shadow-lg h-16 rounded-xl"
             >
                {paymentMethod === 'CARD' ? <Smartphone size={18} className="mr-2"/> : <QrCode size={18} className="mr-2"/>} 
                Process ${finalTotal.toFixed(2)}
             </Button>
          </div>
       </div>

       <AnimatePresence>
           {showCheckoutModal && (
               <CheckoutModal
                   onClose={() => setShowCheckoutModal(false)}
                   onSuccess={handlePaymentSuccess}
                   totalUSD={finalTotal}
                   paymentMethod={paymentMethod}
                   token={paymentMethod !== 'CARD' ? tokenMap.get(paymentMethod) : null}
                   walletAddress={walletAddress}
               />
           )}
       </AnimatePresence>

       <AnimatePresence>
           {showMintTicketModal && (
                <MintTicketModal 
                    onClose={() => setShowMintTicketModal(false)} 
                    onSuccess={() => {}}
                    merchantAddress={walletAddress}
                />
           )}
       </AnimatePresence>

       <AnimatePresence>
           {showSuccessToast && (
               <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: 20 }}
                   className="fixed bottom-10 right-10 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 font-bold text-sm"
               >
                   <CheckCircle size={20} />
                   Order completed successfully!
               </motion.div>
           )}
       </AnimatePresence>

       <AnimatePresence>
           {gatingError && (
               <motion.div
                   initial={{ opacity: 0, y: -20, x: '-50%' }}
                   animate={{ opacity: 1, y: 0, x: '-50%' }}
                   exit={{ opacity: 0, y: -20, x: '-50%' }}
                   className="fixed top-24 left-1/2 bg-red-600 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3 z-[100]"
               >
                   <Lock size={16} />
                   <span className="font-bold text-xs uppercase tracking-widest">{gatingError}</span>
               </motion.div>
           )}
       </AnimatePresence>
    </div>
  );
};
